// ════════════════════════════════════════════════════════════════════════
//  Der Zustand der Führung durch die ersten Schritte der Redaktion
//
//  Die Führung läuft über ALLE Seiten mit: sie beginnt auf /lehrgaenge, geht in
//  einen Lehrgang hinein und muss dabei stehen bleiben. Ihr Zustand liegt
//  deshalb in einem Kontext und nicht in einer Seite - dieselbe Überlegung wie
//  bei `DraftContext` nebenan, nur eine Ebene höher: der Entwurfszustand gehört
//  EINEM Lehrgang, die Führung der Person.
//
//  DER PROVIDER SITZT UM DIE ROUTEN, NICHT IN main.tsx
//
//  Ohne Plattformrecht gibt es keine Führung: ein Konto ohne `platform_admins`
//  sieht in dieser App eine Erklärung und keine Arbeitsfläche. In `main.tsx`
//  müsste der Provider genau das noch einmal selbst entscheiden.
//
//  DIE FAKTEN KOSTEN KEINE ZUSÄTZLICHE ABFRAGE
//
//  `useCurricula` ist die Abfrage, die die Lehrgangsliste ohnehin macht, und sie
//  bringt seit `created_by` alles mit, was die Führung wissen muss.
//
//  KEIN AUTOSPEICHERN
//
//  Geschrieben wird beim Weiterschalten, beim Weglegen und beim Zurückholen -
//  drei Handgriffe, drei Schreibvorgänge. Kein Ereignis, keine Dauer, keine
//  Auswertung.
// ════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useSession } from "@otto/plattform";
import {
  currentStep,
  nextStepKey,
  stepPosition,
  tourChecklist,
  tourRoute,
  tourRunning,
  useSaveTour,
  useTourState,
  type TourCheck as TourCheckBase,
} from "@otto/onboarding";
import { useCurricula } from "../../features/curricula/lib/data.js";
import { factsFrom } from "../../features/onboarding/lib/facts.js";
import {
  tourParams,
  TOUR_STEPS,
  type TourFacts,
  type TourMark,
  type TourStep,
  type TourStepKey,
} from "../../features/onboarding/lib/tour.js";

/** Die Prüfliste dieser App – die Rechnung kommt aus dem Paket. */
export type TourCheck = TourCheckBase<TourStep>;

export type Tour = {
  /** Läuft die Leiste? Falsch heißt: weggelegt oder fertig. */
  readonly running: boolean;
  /** Ist die Führung ganz am Anfang? Dann steht der Willkommenssatz darüber. */
  readonly atStart: boolean;
  /** Die AUFGESCHLAGENE Station: die gewählte, sonst die laufende. */
  readonly step: TourStep;
  /** Alle Stationen mit Haken – die Prüfliste in der Leiste. */
  readonly checklist: readonly TourCheck[];
  /**
   * Steht die aufgeschlagene Station auf dem Lesezeichen?
   *
   * Nur dann gibt es „Angesehen, weiter“: wer eine frühere Station nachliest,
   * darf das Lesezeichen nicht zurückziehen.
   */
  readonly isCurrent: boolean;
  readonly position: { readonly number: number; readonly total: number };
  readonly facts: TourFacts;
  /** Die Zieladresse dieser Station, oder null. */
  readonly route: string | null;
  /** Das hervorzuhebende Bedienelement, oder null. */
  readonly mark: TourMark | null;
  /** Eine Station aufschlagen, um sie noch einmal zu lesen. */
  readonly select: (key: TourStepKey) => void;
  /** Eine angesehene Station abhaken und zur nächsten gehen. */
  readonly advance: () => void;
  /** Die Leiste weglegen – „Später“ und „Fertig“ sind derselbe Vorgang. */
  readonly close: () => void;
  /** Sie zurückholen, vom leisen Verweis im Kopf aus. */
  readonly reopen: () => void;
};

const Context = createContext<Tour | null>(null);

export function TourProvider({
  userId,
  children,
}: {
  userId: string;
  children: ReactNode;
}) {
  const { client } = useSession();
  const courses = useCurricula(client);
  const stored = useTourState(client, userId, "admin");
  const save = useSaveTour(client, userId, "admin");

  // Welche Station AUFGESCHLAGEN ist, steht nicht in der Datenbank.
  //
  // Nachlesen ist kein Fortschritt: wer Station 2 noch einmal öffnet, hat
  // deswegen nicht weniger geschafft. Das Lesezeichen ist eine Höchstmarke und
  // bleibt es - hier liegt nur, was gerade auf dem Bildschirm offen ist.
  const [selected, setSelected] = useState<TourStepKey | null>(null);

  // ── Das Lesezeichen folgt der gemessenen Station ─────────────────────
  //
  // „Die Führung geht nie rückwärts“ hält nur, was DAS LESEZEICHEN trägt - und
  // das schreibt sonst nur ein Knopf. Auf einer gemessenen Station gibt es
  // keinen: sie hakt sich an den Daten ab. Damit stünde hier folgender Ablauf:
  // „Neuer Entwurf“ drücken, die Leiste springt auf Station 3, „Verwerfen“
  // drücken - und sie fällt auf Station 2 zurück, mitsamt dem Haken.
  //
  // Und genau dazu lädt die Station ein: der Verwerfen-Halbsatz steht in ihrem
  // Text, weil sonst niemand den Entwurf anlegt. Eine Führung, die für das
  // Aufräumen bestraft, wozu sie eben aufgefordert hat, ist kaputt.
  //
  // Deshalb dieser eine Schreibvorgang ohne Knopf: nicht Autospeichern, sondern
  // dieselbe Zeile, die „weiter“ geschrieben hätte - ausgelöst von der Sache
  // statt von einem Klick. Genau einmal, denn danach ist das Lesezeichen dort.
  //
  // Die Verwaltung braucht das nicht: dort folgt auf jede gemessene Station ein
  // Blick mit Knopf, und der zieht das Lesezeichen ohnehin vorbei.
  // `useMemo` nicht aus Sparsamkeit, sondern wegen der Identität: beide Werte
  // hängen unten in Abhängigkeitslisten, und ein bei jedem Rendern neues Objekt
  // machte sowohl die Wirkung als auch das Merken darunter wirkungslos.
  const facts = useMemo(() => factsFrom(courses.data, userId), [courses.data, userId]);
  const state = useMemo(() => stored.data ?? { step: null, doneAt: null }, [stored.data]);
  const laufend = currentStep(TOUR_STEPS, state, facts);
  const gespeichert = TOUR_STEPS.findIndex((step) => step.key === state.step);
  const erreicht = TOUR_STEPS.findIndex((step) => step.key === laufend.key);
  const nachzutragen = erreicht > Math.max(gespeichert, 0);

  useEffect(() => {
    // Erst wenn der Stand wirklich gelesen ist: solange die Abfrage läuft, sieht
    // jede Datenlage nach „weiter als das Lesezeichen“ aus, und die Führung
    // schriebe sich beim Laden selbst nach vorn.
    if (!stored.isSuccess || !nachzutragen) return;
    // `doneAt` bleibt, wie es ist: eine weggelegte Leiste soll nicht dadurch
    // zurückkommen, dass nebenher ein Entwurf entstanden ist.
    save.mutate({ step: laufend.key, doneAt: state.doneAt });
    // `save` ist bewusst nicht in der Liste: die Mutation ist bei jedem Rendern
    // ein neues Objekt, und die Wirkung liefe endlos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored.isSuccess, nachzutragen, laufend.key, state.doneAt]);

  // `undefined` (lädt noch) und `null` (keine Zeile) laufen oben bewusst
  // zusammen: in beiden Fällen ist nichts bekannt, und `currentStep` rechnet
  // daraus die erste offene Station. Ein Ladezustand in der Leiste wäre ein
  // Kasten, der für 200 Millisekunden „Einen Moment“ sagt.
  const value = useMemo<Tour>(() => {
    const checklist = tourChecklist(TOUR_STEPS, state, facts);
    const step = checklist.find((e) => e.step.key === selected)?.step ?? laufend;
    const running = tourRunning(stored.data ?? null);
    return {
      running,
      atStart: state.step === null && !facts.hasOwnDraft,
      step,
      checklist,
      isCurrent: step.key === laufend.key,
      // Die Position der LAUFENDEN Station, nicht der aufgeschlagenen: die Zeile
      // sagt, wie weit diese Person ist.
      position: stepPosition(TOUR_STEPS, laufend.key),
      facts,
      route: tourRoute(step, tourParams(facts)),
      // Nur solange die Leiste läuft. Ein Ring ohne die Erklärung daneben ist
      // eine Markierung, die niemand deuten kann.
      //
      // Er folgt der AUFGESCHLAGENEN Station, nicht der laufenden: wer Station 1
      // nachliest, will denselben Knopf wiederfinden, um den es damals ging.
      mark: running ? step.mark : null,
      select: (key) => setSelected(key),
      // Weiterschalten heißt auch: die Wahl aufgeben. Sonst bliebe die Leiste
      // auf der Station stehen, die man gerade abgehakt hat.
      advance: () => {
        setSelected(null);
        save.mutate({ step: nextStepKey(TOUR_STEPS, laufend.key), doneAt: null });
      },
      // Weggelegt wird an der LAUFENDEN Station, nicht an der aufgeschlagenen.
      close: () => save.mutate({ step: laufend.key, doneAt: new Date().toISOString() }),
      reopen: () => {
        setSelected(null);
        save.mutate({ step: laufend.key, doneAt: null });
      },
    };
  }, [facts, state, laufend, stored.data, save, selected]);

  return <TourValueProvider value={value}>{children}</TourValueProvider>;
}

/**
 * Derselbe Kontext mit einem fertigen Wert – für Tests und für alles, was die
 * Leiste ohne Datenbank zeigen soll. Wie `DraftValueProvider`: eine Komponente
 * wird gegen einen Zustand geprüft, nicht gegen eine Abfrage.
 */
export function TourValueProvider({
  value,
  children,
}: {
  value: Tour;
  children: ReactNode;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/**
 * Der Zustand der Führung, oder `null` außerhalb des Providers.
 *
 * Bewusst `null` statt eines Fehlers: `Shell` wird auch in Tests und
 * Zugangsfällen gerendert, in denen es kein Plattformrecht und damit keine
 * Führung gibt. Ein Rahmen, der ohne Führung nicht mehr baut, wäre die falsche
 * Abhängigkeitsrichtung.
 */
export function useTour(): Tour | null {
  return useContext(Context);
}
